/** Add / Edit Contact — v1's 4-tab modal (contacts.js cRender), field for
 * field: Basic / Contact / Details / More. Writes through the Phase 11 door.
 * v2 delta: counties are typed chips (Enter) instead of v1's state→county
 * dropdown chain — same stored data, no 3,000-line county dataset. */
import { useState } from "react";
import {
  C_PROF, C_SRC, C_TAGOPTS, buildContactRecord, draftFrom, emptyDraft,
  type Contact, type ContactDraft,
} from "./api";
import { COUNTIES_BY_STATE, US_STATES } from "./counties";

const C_WA_OPTS: [string, string][] = [["wholesaler", "Wholesaler"], ["agent", "Agent"]];
const C_CTYPE_OPTS: [string, string][] = [
  ["gc", "GC"], ["like_gc", "Like GC"], ["electrician", "Electrician"], ["plumbing", "Plumbing"],
  ["roof", "Roof"], ["ac", "A/C"], ["yard", "Yard"], ["inspector", "Inspector"],
  ["refinisher", "Refinisher"], ["floors", "Floors"], ["staging", "Staging"], ["photographer", "Photographer"],
];
const STATUS_OPTS: [string, string][] = [["active", "Active"], ["inactive", "Inactive"], ["do_not_contact", "Do Not Contact"]];
const PREF_OPTS: [string, string][] = [["phone", "Phone Call"], ["email", "Email"], ["text", "Text/SMS"], ["any", "Any Method"]];
const TIME_OPTS: [string, string][] = [["morning", "Morning"], ["afternoon", "Afternoon"], ["evening", "Evening"], ["anytime", "Anytime"]];

const TABS = ["basic", "contact", "details", "more"] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = { basic: "Basic", contact: "Contact", details: "Details", more: "More" };

export function ContactModal({
  existing,
  onSave,
  onClose,
}: {
  existing: Contact | null;
  onSave: (rec: Record<string, unknown>, oldKey: string | null) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>("basic");
  const [d, setD] = useState<ContactDraft>(() => (existing ? draftFrom(existing) : emptyDraft()));
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof ContactDraft>(k: K, v: ContactDraft[K]) => setD((p) => ({ ...p, [k]: v }));

  const save = () => {
    if (!d.n.trim()) {
      setTab("basic");
      setErr("Name is required");
      return;
    }
    onSave(buildContactRecord(d, existing?.raw), existing?.natKey ?? null);
  };

  const chips = (list: string[], onRemove: (i: number) => void) =>
    list.length ? (
      <div className="ctags editable">
        {list.map((t, i) => (
          <span key={t + i} className="ctag">
            {t}
            <button type="button" onClick={() => onRemove(i)}>×</button>
          </span>
        ))}
      </div>
    ) : null;

  const chipInput = (placeholder: string, add: (v: string) => void) => (
    <input
      placeholder={placeholder}
      onKeyDown={(e) => {
        const v = (e.target as HTMLInputElement).value.trim();
        if (e.key === "Enter" && v) {
          e.preventDefault();
          add(v);
          (e.target as HTMLInputElement).value = "";
        }
      }}
    />
  );

  const fld = (label: string, node: React.ReactNode) => (
    <label className="cmfld">
      <span className="lbl">{label}</span>
      {node}
    </label>
  );

  return (
    <div className="calmodalwrap" onClick={onClose}>
      <div className="calmodal wide cmodal" onClick={(e) => e.stopPropagation()}>
        <header className="calmodalhead">
          <strong>{existing ? "Edit Contact" : "Add Contact"}</strong>
          <button className="close" onClick={onClose}>×</button>
        </header>
        <div className="caltabs ctabs">
          {TABS.map((t) => (
            <button key={t} className={"caltab" + (tab === t ? " active" : "")} onClick={() => setTab(t)}>
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
        <div className="calmodalbody cmbody">
          {tab === "basic" ? (
            <>
              {fld("Name *", <input value={d.n} placeholder="Full name" onChange={(e) => { set("n", e.target.value); setErr(null); }} />)}
              {err ? <div className="calerr">{err}</div> : null}
              {fld("Company", <input value={d.co} placeholder="Company name" onChange={(e) => set("co", e.target.value)} />)}
              <div className="cmrow">
                {fld("Wholesaler/Agent", (
                  <select value={d.wa} onChange={(e) => set("wa", e.target.value)}>
                    <option value="">Select…</option>
                    {C_WA_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ))}
                {fld("Contractor Type", (
                  <select value={d.contractorType} onChange={(e) => set("contractorType", e.target.value)}>
                    <option value="">Select…</option>
                    {C_CTYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ))}
              </div>
              {fld("Proficiency", (
                <div className="cmprofs">
                  {C_PROF.map(([v, l]) => (
                    <button key={v} type="button" className={"cmprof p-" + v + (d.prof === v ? " on" : "")} onClick={() => set("prof", d.prof === v ? "" : v)}>
                      {l}
                    </button>
                  ))}
                </div>
              ))}
              {fld("Lead Source", (
                <select value={d.source} onChange={(e) => set("source", e.target.value)}>
                  <option value="">Select source…</option>
                  {C_SRC.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
            </>
          ) : null}

          {tab === "contact" ? (
            <>
              {fld("Primary Email", <input value={d.emailPrimary} placeholder="email@example.com" onChange={(e) => set("emailPrimary", e.target.value)} />)}
              {fld("Secondary Email", <input value={d.emailSecondary} placeholder="secondary@example.com" onChange={(e) => set("emailSecondary", e.target.value)} />)}
              {fld("Mobile Phone", <input value={d.ph} placeholder="(555) 123-4567" onChange={(e) => set("ph", e.target.value)} />)}
              {fld("Work Phone", <input value={d.phoneWork} placeholder="(555) 987-6543" onChange={(e) => set("phoneWork", e.target.value)} />)}
              {fld("Home Phone", <input value={d.phoneHome} placeholder="(555) 555-5555" onChange={(e) => set("phoneHome", e.target.value)} />)}
            </>
          ) : null}

          {tab === "details" ? (
            <>
              <div className="cmarea">
                <strong className="cmareahead">Working Area</strong>
                {/* v1's state→county chain, back (Tom 2026-08-07: "the state
                    county and cities look up dont work") — the port dropped
                    the dataset to save 3,000 lines and lost the lookup with
                    it. State is a picker; picking one resets counties and
                    cities exactly like v1's cSetState did, because Pinellas
                    does not follow you to Georgia. A state stored by older
                    data that isn't in the dataset still shows, selected. */}
                {fld("State", (
                  <select
                    value={d.workState}
                    onChange={(e) => { set("workState", e.target.value); set("counties", []); set("cities", []); }}
                  >
                    <option value="">Select state…</option>
                    {d.workState && !US_STATES.includes(d.workState) ? <option value={d.workState}>{d.workState}</option> : null}
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ))}
                {fld("Counties", (
                  <>
                    {chips(d.counties, (i) => set("counties", d.counties.filter((_, j) => j !== i)))}
                    {(COUNTIES_BY_STATE[d.workState] ?? []).length ? (
                      <select
                        value=""
                        onChange={(e) => { const v = e.target.value; if (v && !d.counties.includes(v)) set("counties", [...d.counties, v]); }}
                      >
                        <option value="">Add county…</option>
                        {(COUNTIES_BY_STATE[d.workState] ?? []).filter((c) => !d.counties.includes(c)).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      chipInput(d.workState ? "Type county and press Enter" : "Select a state first — or type a county", (v) => !d.counties.includes(v) && set("counties", [...d.counties, v]))
                    )}
                  </>
                ))}
                {fld("Cities", (
                  <>
                    {chips(d.cities, (i) => set("cities", d.cities.filter((_, j) => j !== i)))}
                    {chipInput("Type city and press Enter", (v) => !d.cities.includes(v) && set("cities", [...d.cities, v]))}
                  </>
                ))}
              </div>
              {fld("Street Address", <input value={d.address} placeholder="123 Main St" onChange={(e) => set("address", e.target.value)} />)}
              <div className="cmrow three">
                {fld("City", <input value={d.city} onChange={(e) => set("city", e.target.value)} />)}
                {fld("State", <input value={d.state} onChange={(e) => set("state", e.target.value)} />)}
                {fld("Zip", <input value={d.zip} onChange={(e) => set("zip", e.target.value)} />)}
              </div>
              {fld("Website", <input value={d.website} placeholder="https://example.com" onChange={(e) => set("website", e.target.value)} />)}
              {fld("LinkedIn URL", <input value={d.linkedin} placeholder="https://linkedin.com/in/…" onChange={(e) => set("linkedin", e.target.value)} />)}
              {fld("Tags", (
                <>
                  {chips(d.tags, (i) => set("tags", d.tags.filter((_, j) => j !== i)))}
                  <select value="" onChange={(e) => { const v = e.target.value; if (v && !d.tags.includes(v)) set("tags", [...d.tags, v]); }}>
                    <option value="">Add tag…</option>
                    {C_TAGOPTS.filter((t) => !d.tags.includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {chipInput("Or type custom tag and press Enter", (v) => !d.tags.includes(v) && set("tags", [...d.tags, v]))}
                </>
              ))}
              {fld("Notes", <textarea rows={3} value={d.notes} placeholder="Add notes about this contact…" onChange={(e) => set("notes", e.target.value)} />)}
            </>
          ) : null}

          {tab === "more" ? (
            <>
              {fld("Contact Status", (
                <select value={d.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              {fld("Rating", (
                <div className="cmstars">
                  {[1, 2, 3, 4, 5].map((nn) => (
                    <button key={nn} type="button" className={"cmstar" + (d.rating >= nn ? " on" : "")} onClick={() => set("rating", d.rating === nn ? 0 : nn)}>
                      ★
                    </button>
                  ))}
                </div>
              ))}
              {fld("Preferred Contact Method", (
                <select value={d.preferred} onChange={(e) => set("preferred", e.target.value)}>
                  <option value="">Not Set</option>
                  {PREF_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              {fld("Best Time to Contact", (
                <select value={d.bestTime} onChange={(e) => set("bestTime", e.target.value)}>
                  <option value="">Not Set</option>
                  {TIME_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              {fld("Birthday", <input type="date" value={d.birthday} onChange={(e) => set("birthday", e.target.value)} />)}
              {fld("Next Follow-up Date", <input type="date" value={d.followUp} onChange={(e) => set("followUp", e.target.value)} />)}
            </>
          ) : null}
        </div>
        <footer className="calmodalfoot">
          <button className="vtab" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={save}>{existing ? "Update" : "Create"}</button>
        </footer>
      </div>
    </div>
  );
}
